import { useEffect } from 'react';
import { useWorkspaceStore } from '../store/workspaceStore';
import {
  useTabsStore,
  setRequestTabs,
  setActiveRequestTabId,
  setActiveCollectionTabId,
  setActiveSchemaTabId,
} from '../store/tabsStore';
import {
  useCollectionsStore,
  setSelectedRequest,
  setSelectedCollection,
  setSelectedFolder,
  setEditingItem,
  setEditingSchemaItem,
  setSelectedSchemaEntry,
} from '../store/collectionsStore';
import { saveWorkspaceTabs } from '../services/workspaceTabStore';
import { findParentCollectionId } from '../utils/requestResolve';
import { isFolderTab } from '../utils/collectionTabState';

/**
 * Tab persistence + hydration effects extracted verbatim from App (same effects,
 * dep arrays and call position). The gating refs are App-owned (shared with
 * useTabs + onLogoutReset) and passed in; the useTabs handlers are passed in too.
 *
 * Covers: backfill collectionId on tabs, the workspaceTabStateRef render mirror,
 * heal stale active tab ids, save-outgoing + restore-incoming on workspace
 * switch, debounced persist, beforeunload flush, and collection-tab →
 * selectedCollection sync.
 *
 * @param {{
 *   workspaceTabStateRef: { current: any },
 *   tabsHydratingRef: { current: any },
 *   initializedRef: { current: any },
 *   prevWorkspaceIdForTabsRef: { current: any },
 *   persistTabsTimeoutRef: { current: any },
 *   prevWorkspaceIdForPersistRef: { current: any },
 *   restoreWorkspaceTabs: Function,
 *   closeAllTabs: Function,
 *   scheduleWorkspaceTabPersist: Function,
 *   flushWorkspaceTabPersist: Function,
 * }} deps
 */
export function useTabPersistence({
  workspaceTabStateRef,
  tabsHydratingRef,
  initializedRef,
  prevWorkspaceIdForTabsRef,
  persistTabsTimeoutRef,
  prevWorkspaceIdForPersistRef,
  restoreWorkspaceTabs,
  closeAllTabs,
  scheduleWorkspaceTabPersist,
  flushWorkspaceTabPersist,
}) {
  const collections = useCollectionsStore((s) => s.collections);
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);
  const requestTabs = useTabsStore((s) => s.requestTabs);
  const collectionTabs = useTabsStore((s) => s.collectionTabs);
  const schemaTabs = useTabsStore((s) => s.schemaTabs);
  const dbManagerTabs = useTabsStore((s) => s.dbManagerTabs);
  const tabStripOrder = useTabsStore((s) => s.tabStripOrder);
  const activeRequestTabId = useTabsStore((s) => s.activeRequestTabId);
  const activeCollectionTabId = useTabsStore((s) => s.activeCollectionTabId);
  const activeSchemaTabId = useTabsStore((s) => s.activeSchemaTabId);
  const activeDbManagerTabId = useTabsStore((s) => s.activeDbManagerTabId);
  const activeTabType = useTabsStore((s) => s.activeTabType);

  // Backfill collectionId on tabs opened before collectionId was stored on select.
  useEffect(() => {
    if (!collections.length) return;
    setRequestTabs((prev) => {
      let changed = false;
      const next = prev.map((tab) => {
        if (tab.collectionId || tab.originalId == null) return tab;
        const cid = findParentCollectionId(collections, tab.originalId);
        if (cid) {
          changed = true;
          return { ...tab, collectionId: cid };
        }
        return tab;
      });
      return changed ? next : prev;
    });
  }, [collections]);

  workspaceTabStateRef.current = {
    requestTabs,
    collectionTabs,
    schemaTabs,
    dbManagerTabs,
    tabStripOrder,
    activeRequestTabId,
    activeCollectionTabId,
    activeSchemaTabId,
    activeDbManagerTabId,
    activeTabType,
  };

  // Heal stale active tab ids (e.g. closed tab still referenced after partial persist).
  useEffect(() => {
    if (tabsHydratingRef.current || !initializedRef.current) return;

    if (activeTabType === 'request' && requestTabs.length > 0) {
      const activeExists = requestTabs.some((tab) => tab.id === activeRequestTabId);
      if (!activeExists) {
        setActiveRequestTabId(requestTabs[0].id);
      }
      return;
    }

    if (activeTabType === 'collection' && collectionTabs.length > 0) {
      const activeExists = collectionTabs.some((tab) => tab.id === activeCollectionTabId);
      if (!activeExists) {
        setActiveCollectionTabId(collectionTabs[collectionTabs.length - 1].id);
      }
      return;
    }

    if (activeTabType === 'schema' && schemaTabs.length > 0) {
      const activeExists = schemaTabs.some((tab) => tab.id === activeSchemaTabId);
      if (!activeExists) {
        setActiveSchemaTabId(schemaTabs[schemaTabs.length - 1].id);
      }
    }
  }, [
    activeTabType,
    activeRequestTabId,
    activeCollectionTabId,
    activeSchemaTabId,
    requestTabs,
    collectionTabs,
    schemaTabs,
    tabsHydratingRef,
    initializedRef,
  ]);

  useEffect(() => {
    const workspaceId = currentWorkspace?.id ?? null;
    const prevId = prevWorkspaceIdForTabsRef.current;
    let cancelled = false;

    (async () => {
      if (prevId != null && prevId !== workspaceId) {
        if (persistTabsTimeoutRef.current) {
          clearTimeout(persistTabsTimeoutRef.current);
          persistTabsTimeoutRef.current = null;
        }
        await saveWorkspaceTabs(prevId, workspaceTabStateRef.current);
        if (cancelled) return;
        setSelectedRequest(null);
        setEditingItem(null);
      setEditingSchemaItem(null);
        setSelectedSchemaEntry(null);
        await restoreWorkspaceTabs(workspaceId);
      } else if (prevId === null && workspaceId != null) {
        await restoreWorkspaceTabs(workspaceId);
      } else if (workspaceId == null) {
        closeAllTabs({ clearPersistedResponses: false });
      }
      if (!cancelled) {
        prevWorkspaceIdForTabsRef.current = workspaceId;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentWorkspace?.id, restoreWorkspaceTabs, closeAllTabs, prevWorkspaceIdForTabsRef, persistTabsTimeoutRef, workspaceTabStateRef]);

  useEffect(() => {
    const workspaceId = currentWorkspace?.id;
    if (!workspaceId || !initializedRef.current) return;

    if (prevWorkspaceIdForPersistRef.current !== workspaceId) {
      prevWorkspaceIdForPersistRef.current = workspaceId;
      return;
    }

    scheduleWorkspaceTabPersist();
  }, [
    currentWorkspace?.id,
    requestTabs,
    collectionTabs,
    schemaTabs,
    dbManagerTabs,
    tabStripOrder,
    activeRequestTabId,
    activeCollectionTabId,
    activeSchemaTabId,
    activeDbManagerTabId,
    activeTabType,
    scheduleWorkspaceTabPersist,
    initializedRef,
    prevWorkspaceIdForPersistRef,
  ]);

  useEffect(() => {
    const onBeforeUnload = () => flushWorkspaceTabPersist();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      if (persistTabsTimeoutRef.current) {
        clearTimeout(persistTabsTimeoutRef.current);
      }
    };
  }, [flushWorkspaceTabPersist, persistTabsTimeoutRef]);

  useEffect(() => {
    if (activeTabType !== 'collection' || !activeCollectionTabId) return;
    const tab = collectionTabs.find((t) => t.id === activeCollectionTabId);
    if (!tab?.collectionId) return;
    const col = collections.find((c) => String(c.id) === String(tab.collectionId));
    if (!col) return;
    if (isFolderTab(tab)) {
      // A folder page highlights its folder row, not the collection row.
      setSelectedCollection(null);
      setSelectedFolder((prev) => (String(prev?.id) === String(tab.folderId) && String(prev?.collectionId) === String(col.id)
        ? prev : { id: tab.folderId, name: tab.name, collectionId: col.id }));
      return;
    }
    setSelectedCollection((prev) => (String(prev?.id) === String(col.id) ? prev : col));
    setSelectedFolder(null);
  }, [activeTabType, activeCollectionTabId, collectionTabs, collections]);
}
